import { AnimatePresence, motion } from "framer-motion";
import React from "react";

import UserActivityLog from "@SonarCX/components/molecules/UserActivityLog";
import RecentActivity from "../../../molecules/BaseConversation/RecentActivity";
import ChannelSection from "../../../molecules/ChatModals/AddCustomerModal/ChannelSection";
import CustomerDetailsSection from "../../../molecules/ChatModals/AddCustomerModal/CustomerDetailsSection";
import KnowledgeSection from "../../../molecules/ChatModals/AddCustomerModal/KnowledgeSection";
import TagsSection from "../../../molecules/ChatModals/AddCustomerModal/TagsSection";
import TasksSection from "../../../molecules/ChatModals/AddCustomerModal/TasksSection";
import UserFiles from "../../UserFiles";

import useAccess, { PERMS } from "../../../../hooks/useAccess";
import { useBreakpoint } from "../../../../providers/BreakpointProvider";
import { useCustomerContext } from "../../../../providers/CustomerProvider";
import { useMetaContext } from "../../../../providers/MetaProvider";
import useStyles from "./styles";

interface DetailsTableProps {
  user: APIUser;
  readOnly: boolean;
  canEdit: boolean;
}

const DetailsTable: React.FC<DetailsTableProps> = ({
  user,
  readOnly,
  canEdit,
}) => {
  // Gets props from ContactDetails
  const { handleChange, setOld, onFiles } = useCustomerContext();
  const { metaCache } = useMetaContext();
  const { down770 } = useBreakpoint();
  const { isSmall } = useBreakpoint();
  const hasPermission = useAccess();

  const [loadedMeta, setLoadedMeta] = React.useState(false);

  const classes = useStyles({ smallMedia: isSmall });
  const { id } = user;

  const canViewActivity = React.useMemo(() => {
    if (hasPermission([PERMS.ContactsAudit, PERMS.ConversationsFull])) {
      return true;
    }

    return false;
  }, [hasPermission]);

  // load in meta from hook
  React.useEffect(() => {
    if (loadedMeta || !id) {
      return;
    }

    if (id in metaCache.current) {
      const meta = metaCache.current[id];
      setOld(meta);
      meta.forEach(({ key, value }) => {
        handleChange(value, key);
      });
      setLoadedMeta(true);
    }
  }, [loadedMeta, id, metaCache, handleChange, setOld]);

  return (
    <div className={classes.root}>
      <AnimatePresence mode="wait">
        <motion.div
          key={onFiles ? "on-files" : "profile"}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -10, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className={classes.container}>
            {onFiles ? (
              <div className={classes.section}>
                <UserFiles id={id} />
              </div>
            ) : (
              <>
                <div className={classes.section}>
                  <CustomerDetailsSection
                    user={user}
                    readOnly={readOnly}
                    canEdit={canEdit}
                    external
                  />
                </div>

                <div className={classes.section}>
                  <TagsSection readOnly={readOnly} canEdit={canEdit} />
                </div>

                <div className={classes.section}>
                  <TasksSection userID={user.id} isContact />
                </div>

                {down770 && (
                  <div className={classes.section}>
                    <ChannelSection user={user} canEdit={canEdit} />
                  </div>
                )}

                <div className={classes.section}>
                  <KnowledgeSection
                    user={user}
                    canEdit={canEdit}
                    className={classes.paddedSection}
                  />
                </div>

                {canViewActivity && (
                  <div className={classes.section}>
                    <RecentActivity />
                  </div>
                )}

                <div className={classes.section}>
                  <UserActivityLog user={user} />
                </div>
              </>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default DetailsTable;
